import { useState, useEffect } from 'react';
import { Button, Table } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import { chPerm } from 'src/components/helpers';
import authService from 'src/services/api/auth';
import { UsersContainer, UsersHeader, UsersTableWrapper } from './styles/UsersViewStyles';
import UserEditModal from './components/UserEditModal';
import UserCreateModal from './components/UserCreateModal';

const UsersView = () => {
  const isAdmin = chPerm(['intranetAdmin'], true);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Filter only staff users
  const staffUsers = users.filter((user) => {
    const status = user.status || [];
    return Array.isArray(status) && status.includes('staff');
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await authService.getAllUsers();
      if (response.data.success) {
        setUsers(response.data.allUsers || []);
      }
    } catch (error) {
      console.error('Error getting users:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'No se pudieron cargar los usuarios.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditModalOpen(true);
  };

  const handleCreateUser = () => {
    setCreateModalOpen(true);
  };

  const handleUserUpdated = () => {
    fetchUsers();
    setEditModalOpen(false);
    setEditingUser(null);
  };

  const handleUserCreated = () => {
    fetchUsers();
    setCreateModalOpen(false);
  };

  if (!isAdmin) {
    return (
      <UsersContainer>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <h2>Acceso Restringido</h2>
          <p>No tienes permisos para acceder a esta sección.</p>
        </div>
      </UsersContainer>
    );
  }

  return (
    <UsersContainer>
      <UsersHeader>
        <div>
          <h2>Gestión de Usuarios</h2>
          <p>Administra los usuarios de la intranet</p>
        </div>
        <Button primary icon="plus" content="Crear Usuario" onClick={handleCreateUser} />
      </UsersHeader>

      <UsersTableWrapper>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Cargando usuarios...</p>
          </div>
        ) : staffUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>No hay usuarios de la intranet.</p>
          </div>
        ) : (
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Nombre</Table.HeaderCell>
                <Table.HeaderCell>Email</Table.HeaderCell>
                <Table.HeaderCell>Acciones</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {staffUsers.map((user) => (
                <Table.Row key={user.id}>
                  <Table.Cell>{user.name || 'Sin nombre'}</Table.Cell>
                  <Table.Cell>{user.email || 'Sin email'}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size="small"
                      icon="edit"
                      content="Editar Email"
                      onClick={() => handleEditUser(user)}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </UsersTableWrapper>

      <UserEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingUser(null);
        }}
        user={editingUser}
        onSuccess={handleUserUpdated}
      />

      <UserCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleUserCreated}
      />
    </UsersContainer>
  );
};

export default UsersView;
